import { Fuel } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Loading primitives.
 *
 * Three sizes, one visual language: a fuel-pump icon that pulses inside a
 * navy circle with a rotating conic ring around it. Instantly reads as
 * FuelSync (matches the shell logo mark) and feels operational — not a
 * generic corporate spinner.
 *
 * Use:
 *   <Spinner />              // 24px, inline (buttons, list rows)
 *   <LoadingBlock />         // centered block, for section-level loads
 *   <LoadingBlock label="Loading shift…" />
 *   <PageLoader />           // full-viewport, for route-level Suspense
 */

const sizeMap = {
  sm: { wrap: 'h-6 w-6', inner: 'h-3 w-3', icon: 'h-2.5 w-2.5' },
  md: { wrap: 'h-10 w-10', inner: 'h-5 w-5', icon: 'h-3 w-3' },
  lg: { wrap: 'h-14 w-14', inner: 'h-7 w-7', icon: 'h-4 w-4' },
} as const;

export function Spinner({
  size = 'md',
  className,
}: {
  size?: keyof typeof sizeMap;
  className?: string;
}) {
  const s = sizeMap[size];
  return (
    <span
      className={cn('relative inline-grid place-items-center', s.wrap, className)}
      role="status"
      aria-label="Loading"
    >
      {/* Rotating conic ring around the icon */}
      <span
        aria-hidden="true"
        className={cn('absolute inset-0 animate-spin rounded-full', 'ring-fuel')}
        style={{
          background:
            'conic-gradient(from 0deg, transparent 0deg, transparent 220deg, hsl(215 45% 28% / 0.9) 340deg, hsl(215 45% 28%) 360deg)',
          mask: 'radial-gradient(circle, transparent 55%, black 57%)',
          WebkitMask: 'radial-gradient(circle, transparent 55%, black 57%)',
        }}
      />
      {/* Inner filled disc with the fuel mark, pulsing */}
      <span
        aria-hidden="true"
        className={cn(
          'grid place-items-center rounded-full bg-primary text-primary-foreground shadow-sm',
          'animate-pulse-fuel',
          s.inner,
        )}
      >
        <Fuel className={s.icon} />
      </span>
    </span>
  );
}

export function LoadingBlock({
  label = 'Loading…',
  size = 'md',
  className,
}: {
  label?: string;
  size?: keyof typeof sizeMap;
  className?: string;
}) {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-3 py-8', className)}
      role="status"
      aria-live="polite"
    >
      <Spinner size={size} />
      {label && (
        <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </div>
      )}
    </div>
  );
}

/**
 * Full-viewport loader for route-level Suspense (App.tsx). Keeps the
 * FuelSync wordmark visible so users know where they are while chunks
 * download on cold starts.
 */
export function PageLoader({ label = 'Loading FuelSync…' }: { label?: string }) {
  return (
    <div
      className="grid min-h-screen w-full place-items-center bg-background"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center gap-4">
        <Spinner size="lg" />
        <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </div>
      </div>
    </div>
  );
}
